import { yupResolver } from '@hookform/resolvers/yup';
import type { SelectChangeEvent } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Link,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

// import Popup1099NEC from '@/components/ui/1099PopupNec';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import CustomTextMask from '@/components/ui/CustomTextMask';
import Loader from '@/components/ui/Loader';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { ContactUsType } from '@/types';
import { PartnerType } from '@/types/enumTypes';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const Partner = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnack, setSnackOpen] = useState(false);
  const [isLabelName, setIsLabelName] = useState('Partner Name');

  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Now Accepting <span style="color:#1da94a; font-weight: 600;">Form 1098-E</span> for Tax Year 2024!`,
  //   SubHeading: 'File your student loan interest deductions with ease.  ',
  //   CtaText: ' Get Started Today!',
  //   CtaSubHeading: ``,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1098e`,
  //   isActive: true,
  // };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string().required('Full Name is required'),
      DesignationOfContactPerson: Yup.string().required(
        'Designation of contact person is required'
      ),
      Email: Yup.string()
        .required('Email Address is required')
        .email('Enter valid Email Address'),
      Phone: Yup.string()
        .required('Phone Number is required')
        .matches(
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
          'Please enter a valid phone number'
        ),
      Message: Yup.string().required('Description/Comments is required'),
    },
    [
      // Add Cyclic deps here because when require itself
      ['Phone', 'Phone'],
    ]
  );

  const { handleSubmit, control, setValue, reset } = useForm<ContactUsType>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      ContactUsType,
      any
    >,
    defaultValues: {
      Name: '',
      Email: '',
      Phone: '',
      Message: '',
      DesignationOfContactPerson: '',
      NatureOfBusiness: '',
      PartnerName: '',
      PartnerType: PartnerType.None,
      Token: '',
    },
  });

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: ContactUsType) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: ContactUsType = data;
          postData.Token = token;

          const address = `/api/contactus/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData?.status === 'Success') {
            reset();
            setValue('PartnerType', PartnerType.None);
            setSnackOpen(true);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
    reset();
  };

  const handlePartnerType = (event: SelectChangeEvent) => {
    setValue('PartnerName', '');
    let string = '';
    switch (event.target.value) {
      case PartnerType.Business:
        string = 'Business name';
        break;
      // case PartnerType.TaxProfessional:
      //   string = 'Freelancer or entity';
      //   break;
      case PartnerType.Influencer:
        string = 'Social media account link for reference';
        break;
      case PartnerType.Association:
        string = 'Association name';
        break;
      // case PartnerType.SoftwareProvider:
      //   string = 'Product name ';
      //   break;
      default:
        string = 'Partner name';
        break;
    }

    if (event.target.value === 'TaxProfessional') {
      string = 'Freelancer or entity';
    } else if (event.target.value === 'SoftwareProvider') {
      string = 'Product Name';
    }
    setIsLabelName(string);
  };

  return (
    <AppLayout
      // LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="Become a Partner of TaxZerone and earn today | TaxZerone®"
          description="Join TaxZerone as an affiliate partner and earn 20% commission! Unlimited earning potential for businesses and individuals. Start earning today!"
          keywords=""
          canonical="become-a-partner/"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="2183"
          ogShareImg=""
        />
      }
    >
      {/* <Popup1098E /> */}
      <Loader isLoading={isLoading} />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        sx={{ top: '150px !important' }}
      >
        <Alert
          onClose={handleSnackClose}
          variant="filled"
          severity="success"
          sx={{
            width: '100%',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          Thanks for contacting us! We will be in touch with you shortly.
        </Alert>
      </Snackbar>
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Box sx={{ backgroundColor: '#000' }}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{ px: { xs: 2, md: 0 } }}
          >
            <Box
              sx={{
                textAlign: { xs: 'center', md: 'left' },
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                maxWidth: { xs: 'inherit', lg: 770 },
                minWidth: { xs: 'inherit', md: 530 },
                pl: { xs: 0, md: 6, lg: 8, xl: 12 },
                justifyContent: 'center',
              }}
            >
              <Box
                component="div"
                sx={{
                  mt: { xs: 2, sm: 4, md: 5 },
                  mb: { xs: 2, sm: 4, md: 5 },
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                    fontWeight: 600,
                    color: '#fff',
                    mb: 3,
                  }}
                >
                  Become a Partner
                  <Hidden mdDown>
                    {' '}
                    <br />
                  </Hidden>{' '}
                  of TaxZerone!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#e0e0e0',
                  }}
                >
                  Partner with us and unlock endless opportunities{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>
                  for growth and shared success.
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                  sx={{ mt: 4 }}
                >
                  <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#e0e0e0', mb: 1 }}
                    >
                      Interested!
                    </Typography>
                    <Button
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        zIndex: 99,
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.1)',
                        },
                      }}
                      href="#contact"
                    >
                      Contact Us{' '}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Box>
            <Hidden mdDown>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                  width: '100%',
                }}
              >
                <picture style={{ marginBottom: -7 }}>
                  <source srcSet="/assets/images/partner-img.webp" />
                  <source srcSet="/assets/images/partner-img.png" />
                  <img
                    src="/assets/images/partner-img.png"
                    alt="Become a partner of TaxZerone!"
                    className="img-responsive-banner"
                    width={880}
                    height={725}
                  />
                </picture>
              </Box>
            </Hidden>
            <Hidden mdUp smDown>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  width: '100%',
                  backgroundPosition: 'center center !important',
                  mt: '0 !important',
                }}
              >
                <picture style={{ marginBottom: -7 }}>
                  <source srcSet="/assets/images/partner-img-sm.webp" />
                  <source srcSet="/assets/images/partner-img-sm.png" />
                  <img
                    src="/assets/images/partner-img-sm.png"
                    alt="Become a partner of TaxZerone!"
                    className="img-responsive-banner"
                    width={440}
                    height={365}
                  />
                </picture>
              </Box>
            </Hidden>
          </Stack>
        </Container>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
          <Grid
            container
            spacing={2}
            sx={{ mt: 3, mb: 6, justifyContent: 'center' }}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Who can be a partner?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                TaxZerone offers a variety of partnership programs tailored to
                suit different types of platforms.
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                Whether you’re a tax professional, influencer, business, or
                association, there’s a program designed specifically for
                you.{' '}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Link href="/grow-your-business/" sx={{ textDecoration: 'none' }}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    minHeight: '100%',
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',
                      transform: 'translateY(-1em)',
                      transition: 'all 0.5s ease-out',
                    },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 0,
                      borderTopRightRadius: 12,
                      borderTopLeftRadius: 12,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/business-img.webp" />
                      <source srcSet="/assets/images/business-img.png" />
                      <img
                        src="/assets/images/business-img.png"
                        alt="Businesses"
                        className="partner-card"
                        loading="lazy"
                        width={371}
                        height={235}
                        style={{
                          borderTopRightRadius: 12,
                          borderTopLeftRadius: 12,
                        }}
                      />
                    </picture>
                  </Box>
                  <Box sx={{ p: 2.8, textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { md: 22, sm: 18, xs: 16 },
                        fontWeight: 600,
                        textAlign: 'center',
                        mb: 1,
                      }}
                    >
                      Businesses
                    </Typography>
                    <Typography variant="body1">
                      Expand your services portfolio by partnering with
                      TaxZerone. Provide hassle-free tax filing services to your
                      clients while earning a commission for each filing.
                      Service providers like trucking services and business
                      services are welcome to be a partner
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, minHeight: '100%' }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 0,
                    borderTopRightRadius: 16,
                    borderTopLeftRadius: 12,
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/tax-professionals-img.webp" />
                    <source srcSet="/assets/images/tax-professionals-img.png" />
                    <img
                      src="/assets/images/tax-professionals-img.png"
                      alt="Tax Professionals "
                      className="partner-card"
                      loading="lazy"
                      width={372}
                      height={235}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2.8, textAlign: 'center', mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 22, sm: 18, xs: 16 },
                      fontWeight: 600,
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    Tax Professionals
                  </Typography>
                  <Typography variant="body1">
                    Partner with TaxZerone for Fast, Accurate, and Worry-Free
                    Tax Filing! Handle all your clients&apos; tax filings with
                    confidence and precision. With TaxZerone, you get error-free
                    submissions and free unlimited retransmissions in case of
                    IRS rejections.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Link
                href="/become-a-partner/affliate-program/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    minHeight: '100%',
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;',
                      transform: 'translateY(-1em)',
                      transition: 'all 0.5s ease-out',
                    },
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 0 }}>
                    <picture>
                      <source srcSet="/assets/images/affiliates-img.webp" />
                      <source srcSet="/assets/images/affiliates-img.png" />
                      <img
                        src="/assets/images/affiliates-img.png"
                        alt="Affiliates "
                        loading="lazy"
                        className="partner-card"
                        width={372}
                        height={235}
                        style={{
                          borderTopRightRadius: 12,
                          borderTopLeftRadius: 12,
                        }}
                      />
                    </picture>
                  </Box>
                  <Box sx={{ p: 2.8, textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { md: 22, sm: 18, xs: 16 },
                        fontWeight: 600,
                        textAlign: 'center',
                        mb: 1,
                      }}
                    >
                      Affiliates
                    </Typography>
                    <Typography variant="body1">
                      Promote a trusted tax filing platform, connect your
                      audience with valuable services, and earn commissions as
                      you grow. Whether you&apos;re an influencer in finance,
                      trucking, nonprofits, or the gig economy, or an industry
                      expert, blogger, or YouTuber—this is a great opportunity
                      to monetize your influence while helping others file with
                      confidence.
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, minHeight: '100%' }}
              >
                <Box sx={{ textAlign: 'center', mb: 0 }}>
                  <picture>
                    <source srcSet="/assets/images/association-img.webp" />
                    <source srcSet="/assets/images/association-img.png" />
                    <img
                      src="/assets/images/association-img.png"
                      alt="Associations, Groups or Communities "
                      loading="lazy"
                      className="partner-card"
                      width={372}
                      height={235}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2.8, textAlign: 'center', mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 22, sm: 18, xs: 16 },
                      fontWeight: 600,
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    Associations, Groups or Communities
                  </Typography>
                  <Typography variant="body1">
                    Partner with TaxZerone to offer your members a trusted,
                    IRS-authorized tax filing solution. Provide added value
                    while earning commissions on every successful filing—simple,
                    effective, and rewarding for your organization.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, minHeight: '100%' }}
              >
                <Box sx={{ textAlign: 'center', mb: 0 }}>
                  <picture>
                    <source srcSet="/assets/images/soft-provider-img.webp" />
                    <source srcSet="/assets/images/soft-provider-img.png" />
                    <img
                      src="/assets/images/soft-provider-img.png"
                      alt="Software Providers"
                      className="partner-card"
                      loading="lazy"
                      width={372}
                      height={235}
                      style={{
                        borderTopRightRadius: 12,
                        borderTopLeftRadius: 12,
                      }}
                    />
                  </picture>
                </Box>
                <Box sx={{ p: 2.8, textAlign: 'center', mb: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 22, sm: 18, xs: 16 },
                      fontWeight: 600,
                      textAlign: 'center',
                      mb: 1,
                    }}
                  >
                    Software Providers
                  </Typography>
                  <Typography variant="body1">
                    Integrate TaxZerone with your <br />
                    platform—whether it’s a form builder, CRM, payroll tool, or
                    gig worker app—and offer users a seamless tax filing
                    experience. Add value, boost engagement, and unlock new
                    revenue opportunities.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: 'fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Benefits of being a partner with TaxZerone
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                We believe in mutual benefits and mutual development. By
                partnering with TaxZerone{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>
                you will experience the unlimited opportunity to earn and
                develop your firm
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/tax-platform-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/tax-platform-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/tax-platform-icon.png"
                      alt="Tax Filing Platform"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Tax Filing Platform
                </Typography>
                <Typography variant="body1">
                  Provide clients with seamless tax filing services that boost
                  satisfaction and build trust. Partnering with TaxZerone helps
                  you create lasting relationships.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/unlimited-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/unlimited-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/unlimited-icon.png"
                      alt="Unlimited Earning"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Unlimited Earning
                </Typography>
                <Typography variant="body1">
                  Unlock limitless earning potential with TaxZerone. Earn a 15%
                  commission for every tax filing referral you make, turning
                  your network into a consistent revenue stream.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/tracking-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/tracking-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/tracking-icon.png"
                      alt="Dedicated tracking system"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Dedicated tracking system
                </Typography>
                <Typography variant="body1">
                  Track your referrals, monitor earnings, and access detailed
                  purchase data with ease. Our tracking system ensures full
                  transparency.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/multi-user-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/multi-user-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/multi-user-icon.png"
                      alt="Flexible Multi-User Access"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Flexible Multi-User Access
                </Typography>
                <Typography variant="body1">
                  Assign different roles and permissions for your team. Let one
                  create returns, another review, and a third approve for
                  filing.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/form-based-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/form-based-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/form-based-icon.png"
                      alt="Comprehensive Form Support "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Comprehensive Form Support
                </Typography>
                <Typography variant="body1">
                  Simplify tax filing with 7 form groups and support for over 50
                  forms. TaxZerone provides 30+ features to cover all key
                  business tax forms efficiently.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Responsive Customer Assistance"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Responsive Customer Assistance
                </Typography>
                <Typography variant="body1">
                  You never have to be worried about missing any update or news
                  about your filing. All updates from the IRS will be in your
                  Inbox.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          p: 3,
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="md" className="promotion-bg">
          <Stack
            direction={{ md: 'row', sm: 'row', xs: 'column' }}
            spacing={2}
            sx={{
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 34 },
                  color: { xs: '#000814', sm: '#FFFF00' },
                  fontWeight: 900,
                  mb: 2,
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}
              >
                Limited <br />
                Time Offer
              </Typography>
              <Typography
                variant="body1"
                sx={{ textAlign: 'center', color: { xs: '#333', sm: ' #fff' } }}
              >
                Become a partner today and enjoy an
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                justifyContent: 'right',
                textAlign: 'center',
                alignSelf: 'end',
                alignItems: 'left',
                alignContent: 'space-around',
                m: 'auto',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  textAlign: { xs: 'center', sm: 'center' },
                  fontSize: 20,
                  color: '#333',
                  fontWeight: 600,
                }}
              >
                Additional
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: { xs: 'center', sm: 'center' },
                  fontSize: 48,
                  color: '#262262',
                  fontWeight: 700,
                }}
              >
                5%
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: { xs: 'left', sm: 'right', md: 'center' },
                  fontSize: { xs: 16, sm: 18 },
                  color: '#333',
                  fontWeight: 600,
                }}
              >
                Commission on every referral.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{
              mt: { xs: 2, sm: 6, md: 8 },
              textAlign: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                How to become a member of the partnership of TaxZerone?
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Follow the easy steps to send Authorization of Reporting Agent
              </Typography> */}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: 'transparent',
                  minHeight: '100%',
                  border: 'none',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    width: 80,
                    height: 80,
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    m: 'auto',
                    borderRadius: 50,
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 24, sm: 20, xs: 18 },
                      fontWeight: 600,
                      color: '#fff',
                    }}
                  >
                    1
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 18, sm: 16, xs: 16 },
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Fill Out the Form
                  </Typography>
                  <Typography variant="body1">
                    Click the{' '}
                    <span style={{ fontWeight: 600 }}>Contact Us</span> button
                    to access the form. Enter your details to get started.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{ justifyContent: 'center' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: 'transparent',
                  minHeight: '100%',
                  border: 'none',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    width: 80,
                    height: 80,
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    m: 'auto',
                    borderRadius: 50,
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 24, sm: 20, xs: 18 },
                      fontWeight: 600,
                      color: '#fff',
                    }}
                  >
                    2
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 18, sm: 16, xs: 16 },
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Submit the Form
                  </Typography>
                  <Typography variant="body1">
                    Click <span style={{ fontWeight: 600 }}>Submit</span> to
                    send your information to our team.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{ textAlign: 'center', justifyContent: 'center' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: 'transparent',
                  minHeight: '100%',
                  border: 'none',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#1da94a',
                    width: 80,
                    height: 80,
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    m: 'auto',
                    borderRadius: 50,
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 24, sm: 20, xs: 18 },
                      fontWeight: 600,
                      color: '#fff',
                    }}
                  >
                    3
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 18, sm: 16, xs: 16 },
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    Wait for Our Call
                  </Typography>
                  <Typography variant="body1">
                    A TaxZerone representative will contact you shortly to guide
                    you through onboarding and help you join our Partnership
                    Program.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid
                size={12}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <picture>
                  <source srcSet="/assets/images/line-bg.webp" />
                  <source srcSet="/assets/images/line-bg.webp" />
                  <img
                    src="/assets/images/line-bg.png"
                    loading="lazy"
                    alt=""
                    className="line-bg"
                    width={828}
                    height={100}
                  />
                </picture>
              </Grid>
            </Hidden>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          p: 3,
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        id="contact"
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Grid
            container
            spacing={2}
            sx={{ mt: 2, mb: 5, justifyContent: 'center' }}
          >
            <Grid size={12}>
              {' '}
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  lineHeight: 1.4,
                  textAlign: 'center',
                }}
              >
                Transform your client&apos;s and
                <br />
                audience&apos;s tax filing experience.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  fontWeight: 500,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Partner with us today!
              </Typography>
            </Grid>

            <Grid size={12} sx={{ textAlign: 'center' }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  maxWidth: { xs: '100%', sm: 900 },
                  width: '100%',
                  textAlign: 'center',
                  justifyContent: 'center',
                  alignItems: 'center',
                  m: 'auto',
                  borderRadius: 4,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 2,
                    mb: 3,
                    lineHeight: 1.5,
                    textAlign: 'center',
                  }}
                >
                  Contact Us
                </Typography> */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Name"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Name"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Name"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="DesignationOfContactPerson"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="DesignationOfContactPerson"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Designation of Contact Person"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                      <InputLabel color="secondary">Who are you?</InputLabel>
                      <Controller
                        name="PartnerType"
                        control={control}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <Select
                              native
                              labelId="PartnerType"
                              name="PartnerType"
                              id="PartnerType"
                              label="Who are you?"
                              color="secondary"
                              error={!!error}
                              className={
                                value !== PartnerType.None ? 'prefilled' : ''
                              }
                              fullWidth
                              value={value?.toString()}
                              onChange={(event) => {
                                onChange(event);
                                handlePartnerType(event);
                              }}
                            >
                              <option
                                key={PartnerType.None}
                                value={PartnerType.None}
                              />
                              <option
                                key={PartnerType.Business}
                                value={PartnerType.Business}
                              >
                                {PartnerType.Business}
                              </option>
                              <option
                                key={PartnerType.TaxProfessional}
                                value="TaxProfessional"
                              >
                                {PartnerType.TaxProfessional}
                              </option>
                              <option
                                key={PartnerType.Influencer}
                                value={PartnerType.Influencer}
                              >
                                {PartnerType.Influencer}
                              </option>
                              <option
                                key={PartnerType.Association}
                                value={PartnerType.Association}
                              >
                                {PartnerType.Association}
                              </option>
                              <option
                                key={PartnerType.SoftwareProvider}
                                value="SoftwareProvider"
                              >
                                {PartnerType.SoftwareProvider}
                              </option>
                            </Select>
                          </>
                        )}
                      />
                    </FormControl>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Phone"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Phone"
                          label="*Phone Number"
                          className={value ? 'prefilled' : 'mandatory'}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          InputProps={{
                            inputComponent: CustomTextMask as any,
                            inputProps: {
                              mask: '(#00) 000-0000',
                              maxLength: 14,
                            },
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    {' '}
                    <Controller
                      name="PartnerName"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="PartnerName"
                          className={value ? 'prefilled' : ''}
                          label={isLabelName}
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          inputProps={{
                            maxLength: 150,
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Email"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Email"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Email Address"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="Website"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Website"
                          className={value ? 'prefilled' : ''}
                          label="Website (if applicable)"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {' '}
                    <Controller
                      name="NatureOfBusiness"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="NatureOfBusiness"
                          className={value ? 'prefilled' : ''}
                          label="Nature of Business"
                          variant="outlined"
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{ alignContent: 'space-around', mb: 3 }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      m: 'auto',
                    }}
                  >
                    {' '}
                    <Controller
                      name="Message"
                      control={control}
                      defaultValue=""
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          id="Message"
                          className={value ? 'prefilled' : 'mandatory'}
                          label="*Description/Comments:"
                          multiline
                          rows={4}
                          color="secondary"
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                          sx={{ mb: 0 }}
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Button
                      onClick={handleSubmit(onSubmit)}
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        mt: 3,
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Excited to Partner Up? We Are Too!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Join TaxZerone’s Partner Program to grow your business, earn rewards,
          and offer{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          trusted tax filing solutions to your clients. Let’s succeed together!
        </Typography>

        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href="#contact"
          >
            Let’s Partner Up
          </Button>
        </Box>
      </Box>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the TaxZerone Affiliate Program?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      By recommending our reliable tax filing platform to your
                      network, you can make money through the TaxZerone
                      Affiliate Program.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Once you sign up, you’ll receive a unique referral link.
                      It&apos;s a simple way to earn income from your
                      network—each time someone successfully files their taxes
                      using your link, you get paid.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How can I monitor my earnings and referrals?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      You&apos;ll have access to an affiliate dashboard once you
                      sign up. Clicks, sign-ups, and completed purchases are all
                      included in the comprehensive reports it displays. From
                      your dashboard, you can effortlessly monitor your
                      commissions and referral performance in real-time.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Are there any fees to join the affiliate program?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 0 }}>
                      <span style={{ fontWeight: 600 }}> No</span>, there is no
                      cost to sign up for the TaxZerone Affiliate Program. No
                      hidden fees, subscription fees, or sign-up costs apply.
                      With no initial outlay of funds or commitments, you can
                      begin promoting TaxZerone and receiving commissions.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Can I join if I live in a different country?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1, mb: 0 }}>
                      Yes, the program is open to anyone in the world. However,
                      your referrals should mostly be U.S. taxpayers because
                      TaxZerone manages U.S. tax filings. In this manner, you
                      can successfully market the service to a group of people
                      who will use it.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. Can I use email marketing to promote TaxZerone?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1, mb: 0 }}>
                      <span style={{ fontWeight: 600 }}> Yes</span>, you can use
                      email campaigns to advertise TaxZerone. To ensure that
                      your outreach is both lawful and considerate, please make
                      sure you abide by applicable email laws, such as CAN-SPAM,
                      and send emails only to contacts who have consented to
                      receive your communications.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  6. How do I get in touch with affiliate support?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1, mb: 0 }}>
                      Our committed affiliate support staff is available to help
                      if you need it. You can reach us by email at
                      <Link href="/contact-us/" sx={{ color: '#0202ff' }}>
                        {' '}
                        support@taxzerone.com
                      </Link>{' '}
                      or by using the support link in your affiliate dashboard.
                      We are available to help you succeed and respond to your
                      inquiries.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Partner;
